import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import * as XLSX from "xlsx";
import saveAs from "file-saver";
import { Table, TableModule } from "primeng/table";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FormsModule } from "@angular/forms";
import { CommonModule, DatePipe, formatDate } from "@angular/common";
// fixes/14757: AddTrackingSubmitDto is no longer exported by tracking-list.component (manual add-tracking dialog moved here)
import { TrackingListComponent } from "../tracking-list/tracking-list.component";
import { ClientSideLineTrackingListDto } from "../../../core/models/common/clientSideLineTrackingList.dto";
import { AddProjectWorkflowLineNotesDto } from "../../../core/models/project-workflow/addProjectWorkflowLineNotesDto";
import { AddWorkflowLineTrackingDto } from "../../../core/models/project-workflow/addWorkflowLineTrackingDto";
import { AddWorkflowTrackingRequestDto } from "../../../core/models/project-workflow/addWorkflowTrackingRequestDto";
import { DeleteWorkflowLineTrackingDto } from "../../../core/models/project-workflow/deleteWorkflowLineTracking";
import {
  WeightDto,
  DimensionDto,
} from "../../../core/models/project-workflow/dimensionDataDtos";
import { NonAddedSalesOrderLineDto } from "../../../core/models/project-workflow/getNonAddedProjectWorkflowLineDto";
import { ProjectWorkflowTrackingNumberDetailsDto } from "../../../core/models/project-workflow/GetProjectWorkflowTrackingDetailsDto";
import { ProductionWorkflowLineDto } from "../../../core/models/project-workflow/productionWorkflowLineDto";
import { SubmitLineItemProofRequestDto } from "../../../core/models/project-workflow/submitLineItemProofRequestDto";
import { FileUploadDto } from "../../../core/models/shared/fileUploadDto";
import { ProductionWorkflowLineTrackingDto } from "../../../core/models/project-workflow/productionWorkflowLineTrackingDto";
import { ProjectWorkflowLineDto } from "../../../core/models/project-workflow/projectWorkflowLineDto";
import { TrackingDetailsBindingDto } from "../../../core/models/project-workflow/trackingDetailsBindingDto";
import { TrackingHistoryListDto } from "../../../core/models/project-workflow/trackingHistoryListDto";
import { UpdateManualTrackingStatusDto } from "../../../core/models/project-workflow/updateManualTrackingStatusRequestDto";
import { WorkflowNotificationSettings } from "../../../core/models/project-workflow/workflownotificationSettingDto";
import { WorkflowStatusDto } from "../../../core/models/project-workflow/workflowStatusDto";
import { LoaderService } from "../../../core/services/loader.service";
import { ProjectWorkflowService } from "../../../core/services/project-workflow.service";
import { SharedService } from "../../../core/services/shared.service";
import { WorkflowStatusEnum } from "../../../shared/enums/workflowStatusEnum";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import {
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadModule,
} from "primeng/fileupload";
import { DialogModule } from "primeng/dialog";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { WorkflowLineNotesComponent } from "../workflow-line-notes/workflow-line-notes.component";
import { DatePickerModule } from "primeng/datepicker";
import { HidedynamicsdatePipe } from "../../../shared/pipes/hidedate/hidedynamicsdate.pipe";
import { ChartModule } from "primeng/chart";
import { TrackingListPopupComponent } from "../tracking-list-popup/tracking-list-popup.component";
import { TrackingDetailPopupComponent } from "../tracking-detail-popup/tracking-detail-popup.component";
import { AddWorkflowLinesComponent } from "../add-workflow-lines/add-workflow-lines.component";
import { NotificationSettingsComponent } from "../notification-settings/notification-settings.component";
import { AddTrackingComponent } from "../add-tracking/add-tracking.component";
import {
  isFedexCarrier,
  isUpsCarrier,
} from "../../../core/utilities/carrier-shipping-method";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { PopoverModule } from "primeng/popover";
import { CheckboxModule } from "primeng/checkbox";
import { PdfViewerPageComponent } from "../../../shared/components/pdf-viewer-page/pdf-viewer-page.component";
import { ProofChatMessage } from "../../../shared/components/pdf-viewer-page/proofChatMessage";
import { ProofResubmitEvent } from "../../../shared/components/pdf-viewer-page/pdf-viewer-page.component";
import { LineProofHistoryDto } from "../../../core/models/project-workflow/lineProofHistoryDto";
import { ProofTaskUpdatePromptDto } from "../../../core/models/project-workflow/proofTaskUpdatePromptDto";
import { TooltipModule } from "primeng/tooltip";
import { TagModule } from "primeng/tag";
import { OrderTrackingExcelComponent } from "../../orders/components/order-tracking-excel/order-tracking-excel.component";
import { UpdateProductionTaskModalComponent } from "../update-production-task-modal/update-production-task-modal.component";
import { UpdateProductionTaskPayload } from "../update-production-task-modal/update-production-task-modal.component";
import { SignalRService } from "../../../core/services/signal-r.service";

@UntilDestroy()
@Component({
  selector: "jni-workflow-line-items",
  templateUrl: "./workflow-line-items.component.html",
  styleUrls: ["./workflow-line-items.component.scss"],
  imports: [
    ButtonModule,
    CardModule,
    InputTextModule,
    CommonModule,
    FileUploadModule,
    DialogModule,
    SelectModule,
    WorkflowLineNotesComponent,
    FormsModule,
    TableModule,
    DatePickerModule,
    HidedynamicsdatePipe,
    PopoverModule,
    ChartModule,
    TrackingListPopupComponent,
    TrackingDetailPopupComponent,
    AddWorkflowLinesComponent,
    NotificationSettingsComponent,
    TrackingListComponent,
    AddTrackingComponent,
    SpinnerComponent,
    CheckboxModule,
    TooltipModule,
    TagModule,
    PdfViewerPageComponent,
    OrderTrackingExcelComponent,
    UpdateProductionTaskModalComponent,
  ],
})
export class WorkflowLineItemsComponent implements DoCheck {
  @Input() orderLines = [] as ProductionWorkflowLineDto[];
  @Input() previousData = [] as ProductionWorkflowLineDto[];
  @Input() workflowId = 0;
  @Input() salesOrderIdD365 = "";
  @Input() smallLoader = false;
  @Input() statusList = [] as WorkflowStatusDto[];
  @Input() workflowStatus = 0;
  @Input() summaryPage = true;
  @Input() isDisableLink = false;
  /**
   * Whether the project's client has Client Portal access enabled. When `false`, the Proof
   * (Upload/View Proof) column is hidden. Defaults to enabled.
   */
  @Input() isClientPortalEnabled = true;
  @Output() downloadExcelTemplateEvent = new EventEmitter();
  @Output() refreshWorflowLineItemsEvent = new EventEmitter();
  @Output() refreshGridEvent = new EventEmitter();
  @Output() dropEvent = new EventEmitter<ProductionWorkflowLineDto[]>();
  @Output() showAllLinesEvent = new EventEmitter<boolean>();
  @Output() addLineLoaderEvent = new EventEmitter<boolean>();
  @Output() createProductionTaskEvent =
    new EventEmitter<ProductionWorkflowLineDto>();
  @Output() hasUnsavedChangesEvent = new EventEmitter<boolean>();
  selectedLineItem: ProductionWorkflowLineDto[] = [];
  workflowTrackingList = [] as ProductionWorkflowLineTrackingDto[];
  addTrackingModal = false;
  trackingListModal = false;
  trackingPopupAddMode = false;
  trackingLineQuantity = 0;
  trackingLineRecIdD365 = 0;
  trackingLineShippingMethod = "";
  trackingLineVendorSKU = "";
  trackingDetailsList = [] as ClientSideLineTrackingListDto[];
  trackingDetail = false;
  trackingHistoryModalOpen = false;
  trackingDetailsModalOpen = false;
  trackingHistoryList = [] as TrackingHistoryListDto[];
  historyTrackingNumber = "";
  historyCarrierShippingMethod = "";
  historyShippingMethodName = "";
  historyEstDelivery = "";
  @ViewChild("fileUpload") fileUpload!: FileUpload;
  trackingNumberDetail = {} as ProjectWorkflowTrackingNumberDetailsDto;
  trackingWeight = [] as WeightDto[];
  trackingDimension = [] as DimensionDto[];
  addTrackingLineNumber = 0;
  addTrackingPurchaseOrder = "";
  isUPS = false;
  trackingDetailsBindingObj = {} as TrackingDetailsBindingDto;
  viewSettingsModal = false;
  notificationSettings = [] as WorkflowNotificationSettings[];
  trackingLineShippingMethodName = "";
  @ViewChild("dt") table!: Table;
  descriptionCharacterCond = false;
  showAllLinesFlag = false;
  notificationSettingLineRecId = 0;
  isDelivered = false;
  enabledStatus = 2;
  addWorkflowLinesModalDisplay = false;
  nonAddedLines = [] as NonAddedSalesOrderLineDto[];
  statusListMapping = [
    9, //PROJECTCREATED
    1, //ORDERWRITTEN
    2, //POSENT
    3, //PROOFSENT
    12, //REVISIONREQUESTED
    4, //PROOFAPPROVED
    5, //INPRODUCTION
    6, //SHIPPED
    7, //DELIVERED
  ];
  enabledStatusIndex = 2;
  currentStatus = 0;
  displaySidebarForNotes = false;
  notes = "";
  lineRecId = 0;
  shipDateCurrentdate = new Date();
  inHandDateCurrentdate = new Date();
  // fixes/14757: manual "Add Tracking" dialog fields replaced by the unified add-tracking popup (openAddTrackingFromList)
  // displayAddManualTrackingModal = false;
  // manualShippingMethods = [] as { code: string; shippingMethod: string }[];
  // trackings = "";
  // manualMethod = "";
  smallTrackingListLoader = false;
  @ViewChild("trackingList") trackingListPopup!: TrackingListComponent;
  isExcelUpload = false;
  disableAddTracking = false;
  popupmessage = "";
  dialogStatus = false;
  fedExStatusCategories: Record<string, string[]> = {
    Shipped: [
      "DO",
      "LC",
      "DS",
      "PD",
      "CA",
      "US",
      "RD",
      "IP",
      "HP",
      "RG",
      "RP",
      "RS",
    ],
    "In Transit": [
      "AR",
      "AF",
      "AC",
      "IT",
      "OX",
      "PM",
      "DP",
      "DR",
      "CP",
      "EA",
      "MD",
      "TR",
      "CC",
      "CH",
      "CD",
      "CP",
      "EA",
      "RR",
      "RM",
      "HA",
      "RT",
      "RA",
      "PR",
      "AS",
      "IN",
      "AE",
      "AO",
    ],
    Delivered: ["DL"],
    Exception: ["DD", "DE", "SE", "CD"],
    Delay: ["DY"],
    "Label Created": ["OC"],
    "Picked Up": ["PU"],
    "Out for Delivery": ["OD"],
  };

  constructor(
    private _toastr: JniToastrService,
    private _productionWorkflowService: ProjectWorkflowService,
    private _loaderService: LoaderService,
    private _sharedService: SharedService,
    private readonly _signalRService: SignalRService,
    public datePipe: DatePipe
  ) {}

  ngDoCheck() {
    if (this.orderLines.length > 0) {
      this.orderLines.forEach((item, index) => {
        const check = this._sharedService.deepCompare(
          item,
          this.previousData[index]
        );
        if (check) {
          this.hasUnsavedChangesEvent.emit(true);
        }
      });
    }
  }

  // ngOnChanges(changes: SimpleChanges) {
  //   if(this.orderLines.length > 0){
  //     this.orderLines.forEach(line => {
  //       line.shipDateD365 = this.datePipe.transform(line.shipDateD365, 'MM-dd-yyyy');
  //       line.inHandDate = this.datePipe.transform(line.inHandDate,'MM-dd-yyyy');
  //     });
  //   }
  // }

  showUploadTracking = false;

  // Proof dialog
  @ViewChild(OrderTrackingExcelComponent)
  proofUploader?: OrderTrackingExcelComponent;
  @ViewChild(PdfViewerPageComponent)
  proofViewer?: PdfViewerPageComponent;
  showProofDialog = false;
  showUploadProofDialog = false;
  private readonly proofEligibleStatuses = new Set<number>([
    WorkflowStatusEnum.ProjectCreated,
    WorkflowStatusEnum.OrderWritten,
    WorkflowStatusEnum.PoSent,
    WorkflowStatusEnum.ProofSent,
    WorkflowStatusEnum.RevisionRequested,
    WorkflowStatusEnum.ProofApproved,
    WorkflowStatusEnum.InProduction,
    WorkflowStatusEnum.Shipped,
    WorkflowStatusEnum.Delivered,
    WorkflowStatusEnum.Complete,
  ]);
  private readonly proofNextStageStatuses = new Set<number>([
    WorkflowStatusEnum.InProduction,
    WorkflowStatusEnum.Shipped,
    WorkflowStatusEnum.Delivered,
    WorkflowStatusEnum.Complete,
  ]);
  showReopenProofConfirm = false;
  pendingResubmitEvent: ProofResubmitEvent | null = null;
  proofBase64Pdf = "";
  proofFileName = "proof";
  proofDialogHeader = "";
  selectedProofLine: ProductionWorkflowLineDto | null = null;
  resubmitComment = "";
  resubmitFile: File | null = null;
  resubmitBase64 = "";
  selectedProofLineRecId = 0;
  proofComment = "";
  /**
   * The proof file selected in the current upload dialog. Held on the parent (rather than read from
   * the child uploader) so each line item's selection is reliably captured even after the dialog
   * is closed and re-opened for a different line.
   */
  proofFileToUpload: File | null = null;
  uploadedProofLineRecIds = new Set<number>();
  showUpdateProductionTaskModal = false;
  proofTaskPrompt: ProofTaskUpdatePromptDto | null = null;
  proofTaskPromptLineRecId = 0;
  isUpdatingProductionTask = false;
  isProductionTaskUpdated = false;

  // Proof chat (conversation history rendered in the proof viewer dialog)
  proofChatMessages: ProofChatMessage[] = [];
  proofChatLoading = false;
  proofResubmitting = false;
  proofCanResubmit = false;

  notificationList = ["BC", "BAC", "Admin", "Customer"];
  openedDropdownIndex: number | null = null;

  viewProof(line: ProductionWorkflowLineDto) {
    this.selectedProofLine = line;
    this.selectedProofLineRecId = line.lineRecIdD365;
    const vendorSku = line.externalItemNumberD365?.trim();
    const fileName = line.proofFileName?.trim() ?? "";
    const displayFileName = this.formatProofFileNameForDisplay(fileName);
    const fileLabel = displayFileName ? `(${displayFileName})` : "";
    // The viewer decides whether to render an <img> or the PDF viewer based on the
    // extension of `proofFileName` — without this, it stayed at its default value
    // ("proof", no extension) on the very first view of a line's proof, so image
    // proofs (png/jpg/jpeg) incorrectly fell back to the PDF viewer and rendered blank.
    this.proofFileName = displayFileName || "proof";
    const nonEmpty = (s: string | null | undefined) =>
      s != null && s.trim() != "" ? s.trim() : undefined;
    const nickname = nonEmpty(line.nickName);
    const lineIdentifier =
      nickname ??
      nonEmpty(line.description) ??
      nonEmpty(line.itemNumberD365) ??
      "";

    if (lineIdentifier) {
      this.proofDialogHeader = fileLabel
        ? `${lineIdentifier} - Proof ${fileLabel}`
        : `${lineIdentifier} - Proof`;
    } else if (vendorSku) {
      this.proofDialogHeader = fileLabel
        ? `${vendorSku} - Proof ${fileLabel}`
        : `${vendorSku} - Proof`;
    } else {
      this.proofDialogHeader = fileLabel ? `Proof ${fileLabel}` : "Proof";
    }
    this.proofBase64Pdf = "";
    this.proofBase64Pdf = "";
    this.resubmitComment = "";
    this.resubmitFile = null;
    this.resubmitBase64 = "";
    this.loadProofChat(line.lineRecIdD365);

    if (!line.proofFileUrl) {
      this.showProofDialog = true;
      return;
    }

    this._loaderService.setLoaderState(true);
    this._productionWorkflowService
      .getFileAsBase64(line.proofFileUrl)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (base64: string) => {
          this._loaderService.setLoaderState(false);
          this.proofBase64Pdf = base64;
          this.showProofDialog = true;
        },
        error: () => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", "Unable to load proof document");
        },
      });
  }

  onProofAction(line: ProductionWorkflowLineDto) {
    if (this.isProofDisabledForZeroSell(line)) {
      this._toastr.info(
        this.getProofDisableReason(line),
        "Proof upload unavailable"
      );
      return;
    }

    if (this.isProofUploaded(line)) {
      this.viewProof(line);
      return;
    }

    // Uploading a proof is only allowed when the line is still eligible for proofing.
    if (!this.canUploadProof(line)) {
      return;
    }

    this.selectedProofLine = line;
    this.selectedProofLineRecId = line.lineRecIdD365;
    this.proofComment = "";
    this.proofFileToUpload = null;
    this.showUploadProofDialog = true;
  }

  /** True when the proof is Approved or the line has already moved past approval ("In Next Stage"). */
  isProofReopenable(line: ProductionWorkflowLineDto): boolean {
    return (
      this.getProofStatus(line) === "Approved" ||
      this.proofNextStageStatuses.has(line.lineStatus ?? 0)
    );
  }

  confirmReopenProof(): void {
    const event = this.pendingResubmitEvent;
    this.cancelReopenProof();
    if (event) {
      this.submitProofResubmission(event);
    }
  }

  cancelReopenProof(): void {
    this.showReopenProofConfirm = false;
    this.pendingResubmitEvent = null;
  }

  isProofUploaded(line: ProductionWorkflowLineDto): boolean {
    return (
      line.hasProof === true ||
      this.uploadedProofLineRecIds.has(line.lineRecIdD365)
    );
  }

  // A proof may be uploaded from any active project status — uploading is not gated on PO Sent.
  // Upon a successful upload the backend automatically advances the project status to Proof Sent.
  canUploadProof(line: ProductionWorkflowLineDto): boolean {
    return this.proofEligibleStatuses.has(line.lineStatus ?? 0);
  }

  isProofDisabledForZeroSell(line: ProductionWorkflowLineDto): boolean {
    return (line?.sellD365 ?? 0) === 0;
  }

  getProofDisableReason(line: ProductionWorkflowLineDto): string {
    if (this.isProofDisabledForZeroSell(line)) {
      return "Proof upload is disabled because this line item has a sell value of 0.";
    }

    if (!this.canUploadProof(line)) {
      return "Move the project to PO Sent state before uploading proof.";
    }

    return "";
  }

  submitProofUpload() {
    const file =
      this.proofFileToUpload ?? this.proofUploader?.uploadedFiles?.[0] ?? null;
    const comment = (this.proofUploader?.proofComment ?? "").trim();

    if (!file) {
      this._toastr.error("Please attach a proof file.");
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!["pdf", "png", "jpg", "jpeg"].includes(extension)) {
      this._toastr.error("Only PDF, PNG, JPG, or JPEG files are allowed.");
      return;
    }

    if (!comment) {
      this._toastr.error("Please enter a comment.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      const payload: SubmitLineItemProofRequestDto = {
        projectWorkflowId: this.workflowId,
        lineRecIdD365: this.selectedProofLineRecId,
        comment,
        file: {
          base64,
          fileName: `${file.name.slice(0, file.name.lastIndexOf("."))}_${formatDate(new Date(), "yyyyMMddHHmmss", "en-US", "UTC")}.${extension}`,
          fileSize: file.size,
          fileExtension: extension,
          fileMimeType: file.type,
        },
      };

      this._loaderService.setLoaderState(true);
      this._productionWorkflowService
        .submitLineItemProof(payload)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: response => {
            this._loaderService.setLoaderState(false);
            if (response?.status !== "Success") {
              this.showProofSubmissionFailure(
                response?.message ?? "Unable to submit proof. Please try again."
              );
              return;
            }
            this._toastr.success("Proof submitted successfully.");
            this._signalRService.notifications.set({
              isAllowed: true,
              chatId: 0,
              moduleName: "ProductionWorkflow",
            });
            this.uploadedProofLineRecIds.add(this.selectedProofLineRecId);
            const uploadedLineRecId = this.selectedProofLineRecId;
            const line = this.orderLines.find(
              l => l.lineRecIdD365 === this.selectedProofLineRecId
            );
            if (line) {
              line.hasProof = true;
              line.proofStatus = 1;
              if ((line.lineStatus ?? 0) < WorkflowStatusEnum.ProofSent) {
                line.lineStatus = WorkflowStatusEnum.ProofSent;
              }
            }
            this.closeUploadProofDialog();
            this.refreshWorflowLineItemsEvent.emit();
            this.loadProofTaskUpdatePrompt(uploadedLineRecId);
          },
          error: () => {
            this._loaderService.setLoaderState(false);
            this._toastr.error("Unable to submit proof. Please try again.");
          },
        });
    };
    reader.readAsDataURL(file);
  }

  private showProofSubmissionFailure(detail: string): void {
    if (
      detail ===
        "Proof upload is disabled because this line item has a sell value of 0." ||
      detail ===
        "Proof upload is not allowed for line items with a sell price of 0."
    ) {
      this._toastr.info(detail, "Proof upload unavailable");
      return;
    }

    this._toastr.error(detail);
  }

  closeUploadProofDialog() {
    this.showUploadProofDialog = false;
    this.proofComment = "";
    this.proofFileToUpload = null;
    this.selectedProofLineRecId = 0;
  }

  private loadProofTaskUpdatePrompt(lineRecIdD365: number): void {
    if (lineRecIdD365 === 0) {
      return;
    }

    this._productionWorkflowService
      .getLineItemProofTaskPrompt(this.workflowId, lineRecIdD365)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: response => {
          const prompt = response?.data;
          if (response?.status !== "Success" || !prompt?.hasTask) {
            this.showUpdateProductionTaskModal = false;
            this.proofTaskPrompt = null;
            this.proofTaskPromptLineRecId = 0;
            return;
          }

          this.proofTaskPrompt = prompt;
          this.proofTaskPromptLineRecId = lineRecIdD365;
          this.isProductionTaskUpdated = false;
          this.isUpdatingProductionTask = false;
          this.showUpdateProductionTaskModal = true;
        },
        error: () => {
          this.showUpdateProductionTaskModal = false;
          this.proofTaskPrompt = null;
          this.proofTaskPromptLineRecId = 0;
        },
      });
  }

  onUpdateProductionTask(payload: UpdateProductionTaskPayload): void {
    if (!this.proofTaskPrompt?.hasTask || this.isUpdatingProductionTask) {
      return;
    }

    this.isUpdatingProductionTask = true;
    this._productionWorkflowService
      .updateLineItemProofTaskStatus({
        workflowId: this.workflowId,
        lineRecIdD365: this.proofTaskPromptLineRecId,
        taskId: this.proofTaskPrompt.taskId,
        taskTitle: payload.taskTitle,
        dueDate: payload.dueDate,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: response => {
          this.isUpdatingProductionTask = false;
          if (response?.status !== "Success" || !response.data?.hasTask) {
            this._toastr.error(
              "Unable to update task status. Please try again."
            );
            return;
          }

          this.showUpdateProductionTaskModal = false;
          this.proofTaskPrompt = null;
          this.proofTaskPromptLineRecId = 0;
          this.isProductionTaskUpdated = false;
          this._toastr.success("Production task updated successfully.");
          this.refreshWorflowLineItemsEvent.emit();
          this.refreshGridEvent.emit();
        },
        error: () => {
          this.isUpdatingProductionTask = false;
          this._toastr.error("Unable to update task status. Please try again.");
        },
      });
  }

  onProofTaskModalVisibleChange(visible: boolean): void {
    this.showUpdateProductionTaskModal = visible;
    if (!visible) {
      this.proofTaskPrompt = null;
      this.proofTaskPromptLineRecId = 0;
      this.isUpdatingProductionTask = false;
      this.isProductionTaskUpdated = false;
    }
  }

  /**
   * Resets the proof uploader each time the dialog opens so every line item starts with a clean
   * attachment and comment, preventing a previous line's selection from leaking into the next upload.
   */
  onUploadProofDialogShow(): void {
    this.proofFileToUpload = null;
    this.proofUploader?.resetProofUpload();
  }

  /** Captures the proof file selected in the upload dialog for the active line item. */
  onProofFileSelected(file: File): void {
    this.proofFileToUpload = file;
  }

  closeProofDialog() {
    this.proofViewer?.resetComposer();
    this.showProofDialog = false;
    this.proofBase64Pdf = "";
    this.selectedProofLine = null;
    this.resubmitComment = "";
    this.resubmitFile = null;
    this.resubmitBase64 = "";
    this.proofChatMessages = [];
    this.proofCanResubmit = false;
  }

  /**
   * Loads the proof conversation history for the given line and maps it to chat messages.
   * The existing messages are left in place until the response arrives (instead of being cleared
   * upfront) so that, combined with the chat list's `trackBy`, a resubmission-triggered refresh
   * appends the new message rather than reloading/re-rendering the whole thread.
   */
  private loadProofChat(lineRecIdD365: number) {
    this.proofChatLoading = true;
    this.proofCanResubmit = false;

    this._productionWorkflowService
      .getLineItemProofHistory(this.workflowId, lineRecIdD365)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: response => {
          this.proofChatLoading = false;
          const data = response?.data;
          if (data) {
            this.proofChatMessages = this.mapHistoryToChat(data);
            // Sales can resubmit a new proof version at any time after the first submission so a fresh
            // upload can restart the proof workflow from "Proof Sent" even when the line was already
            // approved or otherwise advanced.
            this.proofCanResubmit = data.currentVersion > 0;
          }
        },
        error: () => {
          this.proofChatLoading = false;
        },
      });
  }

  /** Maps the Nadel Portal proof history (Sales perspective) to chat messages, oldest first. */
  private mapHistoryToChat(data: LineProofHistoryDto): ProofChatMessage[] {
    return [...data.history]
      .sort((a, b) => a.version - b.version || a.id - b.id)
      .map(item => {
        const isSales = item.source === 1;
        const sender =
          item.actorName ??
          item.approverName ??
          (isSales ? "Sales Team" : "Client");
        const isApproved = item.proofStatus === 2;
        const body = isApproved
          ? `Approved By: ${item.approverName ?? item.actorName ?? sender}`
          : (item.comment ?? "");
        return {
          id: item.id,
          initials: this.toInitials(sender),
          sender,
          timestamp: this.formatChatTime(item.createdDate),
          body,
          statusLabel: item.proofStatusLabel ?? "",
          proofStatus: item.proofStatus ?? 0,
          fileName: this.formatProofFileNameForDisplay(item.fileName ?? ""),
          fileUrl: item.fileUrl ?? "",
          // Sales portal perspective: Sales-sourced entries are outgoing.
          outgoing: isSales,
        };
      });
  }

  /** Derives up to two uppercase initials from a display name. */
  private toInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return "?";
    }
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /** Formats an ISO date into the chat timestamp format. */
  private formatChatTime(isoDate: string): string {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  /** Handles a resubmission from the proof chat: uploads a new proof version and refreshes the thread. */
  onResubmitProof(event: ProofResubmitEvent) {
    const line = this.selectedProofLine;
    if (!line || this.proofResubmitting) {
      return;
    }

    // Re-read the live row (the grid may have refreshed since the viewer opened) and fall back to the
    // latest proof history entry so an approved proof is detected even if the row status is stale.
    const currentLine =
      this.orderLines.find(l => l.lineRecIdD365 === line.lineRecIdD365) ?? line;
    const latestChatMessage =
      this.proofChatMessages[this.proofChatMessages.length - 1];
    if (
      this.isProofReopenable(currentLine) ||
      this.isProofReopenable(line) ||
      latestChatMessage?.proofStatus === 2
    ) {
      this.pendingResubmitEvent = event;
      this.showReopenProofConfirm = true;
      return;
    }

    this.submitProofResubmission(event);
  }

  private submitProofResubmission(event: ProofResubmitEvent) {
    const line = this.selectedProofLine;
    if (!line || this.proofResubmitting) {
      return;
    }

    // A revised file is optional on resubmission: when omitted the backend reuses the latest proof document.
    if (!event.file) {
      this.sendProofResubmission(line, event.comment, null);
      return;
    }

    const extension = event.file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!["pdf", "png", "jpg", "jpeg"].includes(extension)) {
      this._toastr.error("Only PDF, PNG, JPG, or JPEG files are allowed.");
      return;
    }

    const file = event.file;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      this.sendProofResubmission(line, event.comment, {
        base64,
        fileName: `${file.name.slice(0, file.name.lastIndexOf("."))}_${formatDate(new Date(), "yyyyMMddHHmmss", "en-US", "UTC")}.${extension}`,
        fileSize: file.size,
        fileExtension: extension,
        fileMimeType: file.type,
      });
    };
    reader.readAsDataURL(file);
  }

  /** Submits a revised proof (with an optional file) and refreshes the chat thread on success. */
  private sendProofResubmission(
    line: ProductionWorkflowLineDto,
    comment: string,
    file: FileUploadDto | null
  ): void {
    const payload: SubmitLineItemProofRequestDto = {
      projectWorkflowId: this.workflowId,
      lineRecIdD365: line.lineRecIdD365,
      comment,
      file,
    };

    this.proofResubmitting = true;
    this._productionWorkflowService
      .submitLineItemProof(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: response => {
          this.proofResubmitting = false;
          if (response?.status !== "Success") {
            this.showProofSubmissionFailure(
              response?.message ??
                "Unable to submit revised proof. Please try again."
            );
            return;
          }
          this._toastr.success("Revised proof submitted successfully.");
          this._signalRService.notifications.set({
            isAllowed: true,
            chatId: 0,
            moduleName: "ProductionWorkflow",
          });
          this.proofViewer?.resetComposer();
          // Update the viewer immediately with the newly submitted file so the
          // user sees the new proof without having to close and reopen the modal.
          if (file) {
            this.proofBase64Pdf = file.base64;
            this.proofFileName = this.formatProofFileNameForDisplay(
              file.fileName
            );
            this.proofDialogHeader = this.formatProofFileNameForDisplay(
              file.fileName
            );
          }
          const target = this.orderLines.find(
            l => l.lineRecIdD365 === line.lineRecIdD365
          );
          if (target) {
            target.hasProof = true;
            target.proofStatus = 1;
            target.lineStatus = WorkflowStatusEnum.ProofSent;
          }
          this.loadProofChat(line.lineRecIdD365);
          this.refreshWorflowLineItemsEvent.emit();
          // Prompt to update the linked production task, mirroring the initial proof-upload flow.
          this.loadProofTaskUpdatePrompt(line.lineRecIdD365);
        },
        error: () => {
          this.proofResubmitting = false;
          this._toastr.error("Unable to resubmit proof. Please try again.");
        },
      });
  }

  private formatProofFileNameForDisplay(fileName: string): string {
    return fileName.replace(
      /_(?:\d{14}|\d{8}_\d{6}_\d{3})(?=(?:\.[^.]*)?$)/,
      ""
    );
  }

  getProofStatus(line: ProductionWorkflowLineDto): string {
    if (
      line.proofStatus === 2 ||
      line.lineStatus === WorkflowStatusEnum.ProofApproved
    ) {
      return "Approved";
    }
    if (
      line.proofStatus === 3 ||
      line.lineStatus === WorkflowStatusEnum.RevisionRequested
    ) {
      return "Change Request";
    }
    if (
      line.hasProof ||
      (line.lineStatus ?? 0) >= WorkflowStatusEnum.ProofSent
    ) {
      return "Pending Approval";
    }
    return "";
  }

  getProofStatusClass(
    line: ProductionWorkflowLineDto
  ):
    | "proof-status-approved"
    | "proof-status-pending"
    | "proof-status-change-request" {
    if (
      line.proofStatus === 2 ||
      line.lineStatus === WorkflowStatusEnum.ProofApproved
    ) {
      return "proof-status-approved";
    }
    if (
      line.proofStatus === 3 ||
      line.lineStatus === WorkflowStatusEnum.RevisionRequested
    ) {
      return "proof-status-change-request";
    }
    return "proof-status-pending";
  }

  getProofStatusIcon(line: ProductionWorkflowLineDto): string {
    if (
      line.proofStatus === 2 ||
      line.lineStatus === WorkflowStatusEnum.ProofApproved
    ) {
      return "pi pi-check-circle";
    }
    if (
      line.proofStatus === 3 ||
      line.lineStatus === WorkflowStatusEnum.RevisionRequested
    ) {
      return "pi pi-refresh";
    }
    return "pi pi-exclamation-circle";
  }
  uploadTracking() {
    this.showUploadTracking = true;
  }

  closeUploadTracking() {
    this.fileUpload.clear();
    this.showUploadTracking = false;
  }

  closeModalDialog() {
    this.fileUpload.clear();
    this.showUploadTracking = false;
    this.trackingListModal = false;
    this.addTrackingModal = false;
    // fixes/14757: this.displayAddManualTrackingModal = false;
    this.trackingListPopup.cancelAllEditRow();
  }

  addTrackingData(
    event: {
      ShippingMethod: string;
      Quantity: number;
      VendorSKU: string;
      LineNumber: number;
      TrackingNumber: string;
      PurchaseOrder: string;
      IsMasterTracking: boolean;
      IsManualTracking?: boolean;
    }[],
    isManualTracking = false
  ) {
    this._loaderService.setLoaderState(true);
    this.showUploadTracking = false;
    if (event.length <= 0) {
      if (this.isExcelUpload) {
        this._toastr.error(
          "Error",
          "Selected Excel does not have any Trackings"
        );
      } else {
        this._toastr.error("Error", "Add a Tracking Number to Save Data");
      }
      this._loaderService.setLoaderState(false);
      this.fileUpload.clear();
      this.hasUnsavedChangesEvent.emit(false);
      return;
    }

    const shippedTracking = this.orderLines.find(
      item =>
        item.lineNumberD365 === event[0]?.LineNumber && //item.lineStatus === WorkflowStatusEnum.Shipped ||
        item.lineStatus === WorkflowStatusEnum.Delivered
    );
    if (shippedTracking) {
      this._toastr.error(
        "Error",
        `Unable to add since other trackings are already delivered.`
      );
      this._loaderService.setLoaderState(false);
      this.fileUpload.clear();
      this.hasUnsavedChangesEvent.emit(false);
      return;
    }

    const payload: AddWorkflowLineTrackingDto[] = event.reduce(
      (acc: AddWorkflowLineTrackingDto[], obj) => {
        const workflowItem = this.orderLines.find(
          item =>
            item.lineNumberD365 === obj.LineNumber &&
            item.externalItemNumberD365 === obj.VendorSKU &&
            item.purchaseOrderD365 === obj.PurchaseOrder &&
            item.isActive === true
        );

        if (workflowItem) {
          const tracking: AddWorkflowLineTrackingDto = {
            vendorSKU: workflowItem.externalItemNumberD365 ?? "",
            quantity: obj.Quantity ?? 0,
            trackingNumber: obj.TrackingNumber ?? "",
            lineRecIdD365: workflowItem.lineRecIdD365 ?? 0,
            workflowId: this.workflowId ?? 0,
            shippingMethodD365:
              obj.ShippingMethod && obj.ShippingMethod !== ""
                ? obj.ShippingMethod
                : (workflowItem.shippingMethodD365 ?? ""),
            salesOrderId: this.salesOrderIdD365 ?? "",
            lineNumberD365: workflowItem.lineNumberD365 ?? "",
            isMasterTracking: obj.IsMasterTracking ?? false,
            isManualTracking: obj.IsManualTracking ?? isManualTracking,
          };

          acc.push(tracking);
        }

        return acc;
      },
      [] as AddWorkflowLineTrackingDto[]
    );

    if (payload.length <= 0) {
      if (!this.isExcelUpload) {
        this._toastr.error(
          "Error",
          "Trackings added do not match with selected Line Item"
        );
      } else {
        this._toastr.error(
          "Error",
          "Excel data does not matches the Line Items, Kindly upload the correct Excel Sheet"
        );
      }
      this._loaderService.setLoaderState(false);
      this.fileUpload.clear();
      this.hasUnsavedChangesEvent.emit(false);
      return;
    }

    const request: AddWorkflowTrackingRequestDto = {
      processWorkflowTrackingDetailsRequestList: payload,
      isManualTracking: isManualTracking,
    } as AddWorkflowTrackingRequestDto;

    this._productionWorkflowService
      .postWorkflowLineTrackingList(request)
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res && res.status === "Success") {
            this._toastr.success("Success", res.message);
            this.hasUnsavedChangesEvent.emit(false);
          } else {
            this._toastr.error("Error", res.message);
          }
          this.isExcelUpload = false;
          this.refreshWorflowLineItemsEvent.emit();
          this.refreshGridEvent.emit();
          this.closeModalDialog();
          return;
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message);
          this.closeModalDialog();
          return;
        },
      });
  }

  onUpload(event: FileUploadHandlerEvent) {
    this._loaderService.setLoaderState(true);
    const file = event.files[0];
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e: ProgressEvent<FileReader>) => {
      /* create workbook */
      const binarystr: string = e.target?.result as string;
      const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: "binary" });

      /* selected the first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      const rows = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1 });
      const headers = rows[0];

      const requiredHeaders = [
        "Quantity",
        "TrackingNumber",
        "LineNumber",
        "VendorSKU",
        "PurchaseOrder",
        "IsMasterTracking",
      ];
      const missingHeaders = requiredHeaders.filter(
        header => !headers.includes(header)
      );

      if (missingHeaders.length > 0) {
        this._loaderService.setLoaderState(false);
        this._toastr.error(
          "Error",
          `Missing requied columns: ${missingHeaders.join(", ")}`
        );
        return;
      }

      /* save data */
      const data = XLSX.utils.sheet_to_json(ws).map((obj: unknown) => {
        const item = obj as {
          Quantity: number;
          VendorSKU: string;
          LineNumber: number;
          TrackingNumber: string;
          PurchaseOrder: string;
          IsMasterTracking: string | boolean;
        };
        return {
          Quantity: item.Quantity ?? 0,
          VendorSKU: String(item.VendorSKU),
          LineNumber: item.LineNumber ?? 0,
          TrackingNumber: String(item.TrackingNumber),
          PurchaseOrder: String(item.PurchaseOrder),
          IsMasterTracking: Boolean(item.IsMasterTracking ?? "false"),
          ShippingMethod: "",
        };
      }); // to get 2d array pass 2nd parameter as object {header: 1}
      this.isExcelUpload = true;
      this.addTrackingData(data);
    };
  }

  isDragOver = false;

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type
      if (
        !file.name.toLowerCase().endsWith(".xlsx") &&
        !file.name.toLowerCase().endsWith(".xls")
      ) {
        this._toastr.error(
          "File Upload Error",
          "Please select an Excel file (.xlsx or .xls)"
        );
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        this._toastr.error(
          "File Upload Error",
          "File size must be less than 10MB"
        );
        return;
      }

      // Add the file to the PrimeNG FileUpload component
      if (this.fileUpload) {
        this.fileUpload.files = [file];
      }
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  removeFile(file: File): void {
    const fileUpload = this.fileUpload;
    if (fileUpload) {
      const index = fileUpload.files?.indexOf(file);
      if (index !== undefined && index > -1) {
        fileUpload.files.splice(index, 1);
      }
    }
  }

  getWorkFlowTrackingExcelTemplate() {
    this._productionWorkflowService
      .getWorkFlowTrackingExcelTemplate()
      .subscribe({
        next: res => {
          //this._loaderService.setLoaderState(false);
          this.downloadFile(res);
        },
        error: res => {
          this._toastr.error("Error", res.message);
          //this._loaderService.setLoaderState(false);
        },
      });
  }

  downloadFile(data: Blob) {
    const file = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(file, `ProductionWorkflowTracking_Template.xlsx`);
  }

  showTrackingPopup(event: string, line?: ProductionWorkflowLineDto) {
    this._loaderService.setLoaderState(true);

    const activeLine = line ?? this.selectedLineItem?.[0];
    if (!activeLine) {
      this._toastr.error("Error", "Select a Line Item to Add Tracking Numbers");
      this._loaderService.setLoaderState(false);
      return;
    }

    this.selectedLineItem = [activeLine];

    if (activeLine && !activeLine.isActive) {
      this._toastr.error("Error", "Cannot Add Tracking for Deleted Line Item");
      this._loaderService.setLoaderState(false);
      return;
    }

    const shippedTracking = this.orderLines.find(
      item =>
        item.lineNumberD365 === activeLine.lineNumberD365 && //item.lineStatus === WorkflowStatusEnum.Shipped ||
        item.lineStatus === WorkflowStatusEnum.Delivered
    );
    if (shippedTracking) {
      this._toastr.error(
        "Error",
        `Unable to add since other trackings are already delivered.`
      );
      this._loaderService.setLoaderState(false);
      return;
    }

    this.trackingLineVendorSKU = activeLine.externalItemNumberD365 ?? "";
    this.trackingLineQuantity = Number(activeLine.quantityD365 ?? 0);
    this.trackingLineRecIdD365 = activeLine.lineRecIdD365 ?? 0;
    this.trackingLineShippingMethod = activeLine.shippingMethodD365 ?? "";
    this.trackingLineShippingMethodName = activeLine.shippingMethod ?? "";

    if (event === "add") {
      this.trackingPopupAddMode = true;
    } else {
      this.trackingPopupAddMode = false;
    }
    this.getWorflowLineTrackingList();
  }

  getWorflowLineTrackingList() {
    this.smallTrackingListLoader = true;
    this._productionWorkflowService
      .getWorkflowLineTrackingList(this.workflowId, this.trackingLineRecIdD365)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.data) {
            this.workflowTrackingList = res.data;
            const shippedTracking = this.workflowTrackingList.find(
              item =>
                //item.deliveryStatus === "SHIPPED" ||
                item.deliveryStatus === "DELIVERED"
            );
            if (shippedTracking) {
              this.disableAddTracking = true;
            } else {
              const orderLine = this.orderLines?.find(
                line => line.lineRecIdD365 === this.trackingLineRecIdD365
              );
              if (
                orderLine && //orderLine.lineStatus === WorkflowStatusEnum.Shipped ||
                orderLine.lineStatus === WorkflowStatusEnum.Delivered
              ) {
                this.disableAddTracking = true;
              } else {
                this.disableAddTracking = false;
              }
            }

            if (this.trackingPopupAddMode) {
              this.addTrackingModal = true;
              this.trackingListModal = false;
              const orderLine = this.orderLines.find(
                x => x.lineRecIdD365 === this.trackingLineRecIdD365
              );
              this.addTrackingLineNumber = orderLine?.lineNumberD365 ?? 0;
              this.addTrackingPurchaseOrder =
                orderLine?.purchaseOrderD365 ?? "";
            } else {
              this.addTrackingModal = false;
              this.trackingListModal = true;
              this.trackingDetailsList = this.workflowTrackingList.map(obj => {
                const tracking = {} as ClientSideLineTrackingListDto;
                tracking.vendorSKU = obj.vendorSKU;
                tracking.quantity = obj.quantity;
                tracking.trackingNumber = obj.trackingNumber;
                tracking.deliveryStatus = obj.deliveryStatus;
                tracking.isFromDatabase = true;
                tracking.shippingMethod = obj.shippingMethod;
                tracking.id = obj.trackingLineId;
                tracking.lineRecIdD365 = this.trackingLineRecIdD365;
                tracking.shippingMethodName = obj.shippingMethodName;
                tracking.carrierShippingMethod = obj.carrierShippingMethod;
                tracking.masterTrackingNumber = obj.masterTrackingNumber;
                tracking.isMasterTracking = obj.isMasterTracking;
                tracking.isManualTracking = obj.isManualTracking;
                if (obj.isManualTracking) {
                  tracking.deliveryStatusId = obj.deliveryStatusId ?? 0;
                } else {
                  tracking.deliveryStatusId = 0;
                }
                return tracking;
              });
              this.trackingDetailsList = this._sharedService.sortTrackingData(
                this.trackingDetailsList
              );
            }
          }
          this._loaderService.setLoaderState(false);
          this.smallTrackingListLoader = false;
        },
        error: res => {
          this._toastr.error("Error", res.message);
          this._loaderService.setLoaderState(false);
          this.smallTrackingListLoader = false;
        },
      });
  }

  deleteTrackingRecord(event: ClientSideLineTrackingListDto) {
    this._loaderService.setLoaderState(true);
    const payload = {} as DeleteWorkflowLineTrackingDto;
    payload.projectWorkflowTrackingLineId = event.id;
    this._productionWorkflowService
      .deleteWorkflowLineTracking(payload)
      .subscribe({
        next: res => {
          if (res?.data) {
            this.refreshWorflowLineItemsEvent.emit();
            this._toastr.success("Success", res.message);
          } else {
            this._toastr.error("Error", "Failed to Delete Record");
          }
          this._loaderService.setLoaderState(false);
        },
        error: res => {
          this._toastr.error("Error", res.message);
          this._loaderService.setLoaderState(false);
        },
      });
  }

  showTrackingDetailList(event: ProductionWorkflowLineDto) {
    this._loaderService.setLoaderState(true);

    this.selectedLineItem = [event];
    this.trackingLineVendorSKU = event.externalItemNumberD365;
    this.trackingLineQuantity = Number(event.quantityD365 ?? 0);
    this.trackingLineRecIdD365 = event.lineRecIdD365;
    this.trackingLineShippingMethod = event.shippingMethodD365 ?? "";
    this.trackingLineShippingMethodName = event.shippingMethod ?? "";
    this.trackingPopupAddMode = false;
    this.getWorflowLineTrackingList();
  }

  showTrackingHistory(event: ClientSideLineTrackingListDto) {
    this._productionWorkflowService
      .getTrackingHistoryByTrackingNumber(
        event.trackingNumber,
        this.trackingLineRecIdD365
      )
      .subscribe({
        next: res => {
          if (res?.data) {
            this.trackingHistoryList = res.data?.map(obj => {
              const mappedData = {} as TrackingHistoryListDto;
              mappedData.createdDate = obj.eventDate ?? "";
              mappedData.message =
                (obj.eventDescription ? obj.eventDescription + ", " : "") +
                (obj.address ? obj.address + ", " : "") +
                (obj.locationType ?? "");
              if (
                isFedexCarrier(
                  event.carrierShippingMethod,
                  event.shippingMethod
                ) &&
                obj.derivedStatusCode === obj.eventCode
              ) {
                mappedData.status =
                  Object.keys(this.fedExStatusCategories).find(category =>
                    this.fedExStatusCategories[category].includes(
                      obj.derivedStatusCode ?? ""
                    )
                  ) ??
                  obj.derivedStatus ??
                  "";
              } else {
                mappedData.status = obj.derivedStatus ?? "";
              }

              return mappedData;
            });
            this.historyTrackingNumber = event.trackingNumber;
            this.historyShippingMethodName = event.shippingMethodName;
            this.historyCarrierShippingMethod =
              event.carrierShippingMethod ?? "";
            const historyOrderLine = this.orderLines.find(
              x => x.lineRecIdD365 === event.lineRecIdD365
            );
            this.historyEstDelivery = historyOrderLine?.inHandDate ?? "";
            this.trackingHistoryModalOpen = true;
          } else {
            this._toastr.error("Error", "Unable to get Tracking History");
          }
        },
        error: () => {
          this._toastr.error("Error", "Unable to get Tracking History");
        },
      });
  }

  closeModal() {
    this.addTrackingModal = false;
    this.trackingListModal = false;
  }

  openAddTrackingFromList() {
    const orderLine = this.orderLines.find(
      x => x.lineRecIdD365 === this.trackingLineRecIdD365
    );
    this.addTrackingLineNumber = orderLine?.lineNumberD365 ?? 0;
    this.addTrackingPurchaseOrder = orderLine?.purchaseOrderD365 ?? "";
    this.trackingListModal = false;
    this.addTrackingModal = true;
  }

  closeTrackingModals() {
    this.trackingHistoryModalOpen = false;
    this.trackingDetailsModalOpen = false;
  }

  showTrackingDetails(event: ClientSideLineTrackingListDto) {
    this.getTrackingDetails(event.trackingNumber);
  }

  viewTimelineFromShipmentDetails() {
    const event: ClientSideLineTrackingListDto = {
      id: 0,
      trackingNumber: this.trackingDetailsBindingObj.trackingNumber ?? "",
      quantity: this.trackingDetailsBindingObj.quantityD365 ?? 0,
      shippingMethod: this.trackingDetailsBindingObj.shippingMethod ?? "",
      vendorSKU: this.trackingDetailsBindingObj.vendorSkuD365 ?? "",
      deliveryStatus: this.trackingDetailsBindingObj.deliveryStatus ?? "",
      isFromDatabase: true,
      lineRecIdD365: this.trackingLineRecIdD365,
      shippingMethodName: this.trackingDetailsBindingObj.shippingMethod ?? "",
      carrierShippingMethod:
        this.trackingDetailsBindingObj.carrierShippingMethod ?? "",
      isMasterTracking: false,
      isManualTracking: false,
      deliveryStatusId: 0,
    };
    this.trackingDetailsModalOpen = false;
    this.showTrackingHistory(event);
  }

  showLineNotifaction(index: number, event: Event) {
    event.preventDefault();
    this.openedDropdownIndex =
      this.openedDropdownIndex === index ? null : index;
  }

  getTrackingDetails(trackingNumber: string) {
    if (trackingNumber) {
      this._productionWorkflowService
        .getTrackingDetailsByTrackingNumber(
          trackingNumber,
          this.trackingLineRecIdD365
        )
        .subscribe({
          next: res => {
            if (res?.data) {
              this.trackingNumberDetail = res.data;
              if (
                res?.data.deliveryStatus?.toLowerCase() !== "pending" &&
                res?.data.deliveryStatus !== ""
              ) {
                if (res?.data?.weight && res?.data?.weight !== "") {
                  // weight can serialize to the literal "null", guard against overwriting the array with null
                  this.trackingWeight =
                    (JSON.parse(res?.data?.weight ?? "") as WeightDto[]) ?? [];
                }
                if (res?.data?.weight && res?.data?.dimension !== "") {
                  this.trackingDimension =
                    (JSON.parse(
                      res?.data?.dimension ?? ""
                    ) as DimensionDto[]) ?? [];
                }
              }
              this.mapTrackingNumberDetailData();
              if (
                isUpsCarrier(
                  this.trackingNumberDetail.carrierShippingMethod,
                  this.trackingNumberDetail.shippingMethodD365
                )
              ) {
                this.isUPS = true;
              }
              this.trackingDetailsModalOpen = true;
            } else {
              this._toastr.error("Error", "No Tracking details Found");
            }
          },
          error: res => {
            this._toastr.error("Error", res.message);
          },
        });
    }
  }

  mapTrackingNumberDetailData() {
    this.trackingDetailsBindingObj = {
      workFlowId: this.trackingNumberDetail.workFlowId ?? 0,
      actualDateTime: this.trackingNumberDetail.actualDateTime ?? "",
      address: this.trackingNumberDetail.address ?? "",
      shipperInformation:
        this.getStringWithComma(this.trackingNumberDetail.shipperCity ?? "") +
        this.getStringWithComma(
          this.trackingNumberDetail.shipperProvinceCode ?? ""
        ) +
        this.getStringWithComma(
          this.trackingNumberDetail.shipperCountryName ?? "",
          false
        ),
      recipientInformation:
        this.getStringWithComma(this.trackingNumberDetail.recipientCity ?? "") +
        this.getStringWithComma(
          this.trackingNumberDetail.recipientProvinceCode ?? ""
        ) +
        this.getStringWithComma(
          this.trackingNumberDetail.recipientCountryName ?? "",
          false
        ),
      vendorSkuD365: this.trackingNumberDetail.vendorSkuD365 ?? "",
      status: this.trackingNumberDetail.status ?? "",
      quantityD365: this.trackingNumberDetail.quantityD365 ?? 0,
      deliveryStatus: this.trackingNumberDetail.deliveryStatus ?? "",
      description: this.trackingNumberDetail.description ?? "",
      shippingMethodD365: this.trackingNumberDetail.shippingMethodD365,
      shippingMethod: this.trackingNumberDetail.shippingMethod,
      carrierShippingMethod: this.trackingNumberDetail.carrierShippingMethod,
      trackingNumber: this.trackingNumberDetail.trackingNumber,
    };

    const weightString = [] as string[];
    const dimesionString = [] as string[];
    this.trackingWeight.forEach(element => {
      if (this.isUPS) {
        weightString.push(
          (element.Weight ?? "") + " " + element.UnitOfMeasurement
        );
      } else {
        weightString.push((element.Value ?? "") + " " + element.Unit);
      }
    });
    this.trackingDimension.forEach(element => {
      const unitOfDimension = element.UnitOfDimension ?? "";
      const units = element.Units ?? "";
      if (this.isUPS) {
        dimesionString.push(
          "Length: " +
            (element.Length ?? 0) +
            " " +
            unitOfDimension +
            ", Width: " +
            (element.Width ?? 0) +
            " " +
            unitOfDimension +
            ", Height: " +
            (element.Height ?? 0) +
            " " +
            unitOfDimension
        );
      } else {
        dimesionString.push(
          "Length: " +
            (element.Length ?? 0) +
            " " +
            units +
            ", Width: " +
            (element.Width ?? 0) +
            " " +
            units +
            ", Height: " +
            (element.Height ?? 0) +
            " " +
            units
        );
      }
    });
    this.trackingDetailsBindingObj.weight = weightString;
    this.trackingDetailsBindingObj.dimension = dimesionString;
  }

  getStringWithComma(value: string | null, isCommaApplied = true) {
    return value && value !== "" ? value + (isCommaApplied ? ", " : "") : "";
  }

  getNotificationSettings(line: ProductionWorkflowLineDto) {
    this._loaderService.setLoaderState(true);
    this._productionWorkflowService
      .getWorkflowNotificationSettings(
        this.workflowId,
        false,
        line?.lineRecIdD365 ?? 0,
        true
      )
      .subscribe({
        next: res => {
          if (res?.data) {
            this.notificationSettings = res?.data;
            this.notificationSettingLineRecId = line?.lineRecIdD365 ?? 0;
            this.showOrHideNotificationSettings();
            this._loaderService.setLoaderState(false);
          }
          this._loaderService.setLoaderState(false);
        },
        error: () => {
          this._toastr.error("Error", "Unable to fetch notification settings");
          this._loaderService.setLoaderState(false);
        },
      });
  }

  showOrHideNotificationSettings() {
    this.viewSettingsModal = !this.viewSettingsModal;
  }

  hideNotificationSettingDailog(event: boolean) {
    this.viewSettingsModal = event;
  }

  cancelAllEditRow() {
    this.table.value.forEach(x => {
      this.table.cancelRowEdit(x);
    });
  }

  editLine(id: number) {
    this.cancelAllEditRow();
    const row = this.table.value.filter(x => x.lineRecIdD365 === id);
    if (row.length > 0) {
      this.table.initRowEdit(row[0]);
    }
  }

  onRowEditSave(line: ProductionWorkflowLineDto) {
    this._loaderService.setLoaderState(true);

    if (line.shipDateD365 && line.inHandDate) {
      if (
        new Date(new Date(line.inHandDate).setHours(0, 0, 0, 0)) <
        new Date(new Date(line.shipDateD365).setHours(0, 0, 0, 0))
      ) {
        this._loaderService.setLoaderState(false);
        this.refreshWorflowLineItemsEvent.emit();
        this._toastr.error(
          "Error",
          "Ship date should be less than or equal to InHand date"
        );
        return;
      }
    }

    const payload: ProjectWorkflowLineDto = {
      projectWorkflowId: this.workflowId,
      lineRecId: line.lineRecIdD365,
      nickName: line.nickName ?? "",
      status: line.lineStatus,
      esd: line.shipDateD365,
      inHandDate: line.inHandDate,
      isFirmInHand: line.isFirmInHand,
    };

    this._productionWorkflowService
      .updateProjectWorkflowLines(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          this.cancelAllEditRow();
          if (res.status.toLowerCase() == "success") {
            this._toastr.success(
              "Success",
              res.message || "Line Item updated successfully"
            );
          } else {
            this._toastr.error(
              "Error",
              res.message || "Error updating Line Item"
            );
          }
          this.refreshWorflowLineItemsEvent.emit();
          this.refreshGridEvent.emit();
          this.hasUnsavedChangesEvent.emit(false);
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastr.error(
            "Error",
            res.message || "Error updating Line Item"
          );
          this.refreshWorflowLineItemsEvent.emit();
        },
      });
  }

  getOddEvenNumber(lineNumber: number) {
    if (lineNumber % 2 === 0) {
      return true;
    } else {
      return false;
    }
  }

  // Preserves row DOM identity across data refreshes (e.g. SignalR tracking updates)
  // so in-progress clicks (like the Tracking link) aren't lost when rows are recreated.
  trackByLineRecId(_: number, line: ProductionWorkflowLineDto) {
    return line.lineRecIdD365;
  }

  descriptionCharacterCount(obj: string) {
    if (obj && obj.length > 45) this.descriptionCharacterCond = true;
  }

  onDrop() {
    this.smallLoader = true;
    this.cancelAllEditRow();
    if (this.selectedLineItem?.length) {
      let lineNumbers = "";
      const filtered = this.selectedLineItem.filter(function (
        ele: ProductionWorkflowLineDto
      ) {
        if (
          ele.lineRecIdD365 !== undefined &&
          ele.lineRecIdD365 !== null &&
          ele.trackingCount <= 0
        ) {
          return true;
        } else {
          lineNumbers += ele.lineNumberD365 + " - " + ele.nickName + ", ";
          return false;
        }
      });
      if (filtered.length > 0 && lineNumbers.length <= 0) {
        this.showAllLinesFlag = false;
        this.dropEvent.emit(filtered);
        this.smallLoader = false;
      } else {
        this._toastr.error(
          "Error",
          `Selected Lines ${lineNumbers} cannot be deleted as they contain Tracking Numbers `
        );
        this.smallLoader = false;
        return;
      }
    } else {
      this._toastr.error("Error", "Select Lines for deletion");
      this.smallLoader = false;
      return;
    }
  }

  uncheckAll() {
    this.selectedLineItem = [];
  }

  toggleShowAllLines() {
    this.showAllLinesFlag = !this.showAllLinesFlag;

    if (this.showAllLinesFlag) {
      this.showAllLinesEvent.emit(true);
    } else {
      this.showAllLinesEvent.emit(false);
    }
  }

  getStatus(status: number) {
    if (this.statusList && status != null) {
      return this.statusList.find(x => x.id == status)?.status;
    }
    return "";
  }

  isStatusDisabled(status: number) {
    this.orderLines.every(line => line.lineStatus === status);
  }

  getNonAddedSalesOrderLines() {
    this.setAddLineLoader(true);
    this._productionWorkflowService
      .getNonAddedSalesOrderLines(this.workflowId, this.salesOrderIdD365)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.data && res?.data.length > 0) {
            this.nonAddedLines = res.data;
          } else {
            this.nonAddedLines = [];
          }
          this.addWorkflowLinesModalDisplay = true;
          this.setAddLineLoader(false);
        },
        error: () => {
          this._toastr.error(
            "Error",
            "Attach a Sales Order to Import Line Items"
          );
          this.setAddLineLoader(false);
        },
      });
  }

  addLinesToProjectWorkflow(event: number[]) {
    this.setAddLineLoader(true);
    const request = {
      workflowId: this.workflowId,
      salesOrderId: this.salesOrderIdD365,
      lineRecIds: event,
    };
    this._productionWorkflowService
      .postAddLineToProjectWorkflow(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.data) {
            this.addWorkflowLinesModalDisplay = false;
            this.refreshWorflowLineItemsEvent.emit();
            this.refreshGridEvent.emit();
          }
        },
        error: () => {
          this._toastr.error("Error", "Unable to add Lines to Project");
          this.setAddLineLoader(false);
        },
      });
  }

  closeAddLineModal() {
    this.addWorkflowLinesModalDisplay = false;
  }

  private setAddLineLoader(isLoading: boolean) {
    this.smallLoader = isLoading;
    this.addLineLoaderEvent.emit(isLoading);
  }

  showInputFieldForLineNote(lineRecId: number, notes: string) {
    this.notes = notes;
    this.lineRecId = lineRecId;
    this.displaySidebarForNotes = true;
  }

  closeSideBarForNotes() {
    this.displaySidebarForNotes = false;
  }

  saveNotes(request: AddProjectWorkflowLineNotesDto) {
    this._loaderService.setLoaderState(true);

    const payload: AddProjectWorkflowLineNotesDto = {
      projectWorkflowId: request.projectWorkflowId,
      lineRecId: request.lineRecId,
      notes: request.notes,
    };

    this._productionWorkflowService
      .addProjectWorkflowLineNotes(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.status.toLowerCase() == "success") {
            this._toastr.success(
              "Success",
              res.message || "notes update successfully"
            );
            this.displaySidebarForNotes = false;
            this.refreshWorflowLineItemsEvent.emit();
          } else {
            this._toastr.error("Error", res.message || "Error updating notes");
          }
          this.refreshWorflowLineItemsEvent.emit();
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message || "Error updating notes");
          this.refreshWorflowLineItemsEvent.emit();
        },
      });
  }

  onShipDateSelection(event: Date, orderLine: ProductionWorkflowLineDto) {
    if (orderLine.shipDateD365 && orderLine.inHandDate) {
      if (
        new Date(new Date(orderLine.shipDateD365).setHours(0, 0, 0, 0)) >
        new Date(new Date(orderLine.inHandDate).setHours(0, 0, 0, 0))
      ) {
        this._toastr.error(
          "Error",
          "Ship date should be less than or equal to InHand date"
        );
        return;
      }
    }

    const date = new Date(event);
    orderLine.shipDateD365 = this.datePipe.transform(date, "MM-dd-yyyy") ?? "";
    if (orderLine.shipDateD365) {
      this.inHandDateCurrentdate = new Date(orderLine.shipDateD365);
    } else {
      this.inHandDateCurrentdate = new Date();
    }

    this.onRowEditSave(orderLine);
  }

  onInHandDateSelection(event: Date, orderLine: ProductionWorkflowLineDto) {
    if (orderLine.inHandDate && orderLine.shipDateD365) {
      if (
        new Date(new Date(orderLine.inHandDate).setHours(0, 0, 0, 0)) <
        new Date(new Date(orderLine.shipDateD365).setHours(0, 0, 0, 0))
      ) {
        this._toastr.error(
          "Error",
          "InHand date should be greater than or equal to Ship date"
        );
        return;
      }
    }

    const date = new Date(event);
    orderLine.inHandDate = this.datePipe.transform(date, "MM-dd-yyyy") ?? "";
  }

  onCalendarShow(orderLine: ProductionWorkflowLineDto) {
    // Wait for the calendar to be fully rendered, then insert the checkbox
    this.addFirmCheckbox(orderLine);

    // Add event listeners to detect when the calendar view changes
    const calendarPopup = document.querySelector(".p-datepicker");

    if (calendarPopup) {
      calendarPopup.addEventListener("click", event => {
        const target = event.target as HTMLElement;
        if (
          target.classList.contains("p-datepicker-next-icon") ||
          target.classList.contains("p-datepicker-prev-icon")
        ) {
          setTimeout(() => {
            this.addFirmCheckbox(orderLine);
          }, 1000);
        }
      });
    }
  }

  addFirmCheckbox(orderLine: ProductionWorkflowLineDto) {
    const calendarPopup = document.querySelector(
      ".p-datepicker .p-datepicker-group"
    );

    if (calendarPopup) {
      // Remove any existing checkbox to avoid duplicates
      const existingCheckbox = document.getElementById("calendar-checkbox");
      if (existingCheckbox) {
        existingCheckbox.parentElement?.remove();
      }

      const checkboxContainer = document.createElement("div");
      checkboxContainer.style.marginTop = "10px";
      checkboxContainer.style.textAlign = "left";

      checkboxContainer.innerHTML = `
        <label>
          <input type="checkbox" id="calendar-checkbox" ${orderLine.isFirmInHand ? "checked" : ""}>
          Firm
        </label>
      `;

      calendarPopup.appendChild(checkboxContainer);

      const checkboxElement = document.getElementById(
        "calendar-checkbox"
      ) as HTMLInputElement;

      checkboxElement.addEventListener("change", event => {
        orderLine.isFirmInHand = (event.target as HTMLInputElement).checked;
      });
    }
  }

  getLineStatusHighlight(status: number) {
    if (
      status === WorkflowStatusEnum.PoSent ||
      status === WorkflowStatusEnum.ProofSent ||
      status === WorkflowStatusEnum.RevisionRequested
    ) {
      return true;
    }
    return false;
  }

  getLineESDClass(line: ProductionWorkflowLineDto) {
    if (
      (line.lineStatus === WorkflowStatusEnum.ProofApproved ||
        line.lineStatus === WorkflowStatusEnum.InProduction) &&
      (!line.shipDateD365 || line.shipDateD365 === "")
    ) {
      return "warning";
    }

    if (line.shipDateD365 && line.shipDateD365 != "") {
      const shipDate = new Date(
        new Date(line.shipDateD365).setHours(0, 0, 0, 0)
      );
      const shippedDate = new Date(
        new Date(line.shippedDate ?? "").setHours(0, 0, 0, 0)
      );
      if (
        (shipDate < new Date(new Date().setHours(0, 0, 0, 0)) &&
          line.trackingCount == 0) ||
        shipDate < shippedDate
      ) {
        return "danger";
      }
    }

    return "";
  }

  getLineIHDClass(line: ProductionWorkflowLineDto) {
    const today = new Date().setHours(0, 0, 0, 0);
    const inHandDate = line.inHandDate
      ? new Date(line.inHandDate).setHours(0, 0, 0, 0)
      : null;
    const shipDate = line.shipDateD365
      ? new Date(line.shipDateD365).setHours(0, 0, 0, 0)
      : null;
    const deliveredDate = line.deliveryDate
      ? new Date(line.deliveryDate).setHours(0, 0, 0, 0)
      : today;
    if (
      line.isFirmInHand &&
      ((inHandDate && inHandDate < deliveredDate && line.trackingCount > 0) ||
        (inHandDate && inHandDate < deliveredDate) ||
        (shipDate && inHandDate && shipDate > inHandDate))
    ) {
      return "firm-danger";
    } else if (
      line.isFirmInHand ||
      (inHandDate && inHandDate < deliveredDate && line.trackingCount > 0) ||
      (inHandDate && inHandDate < deliveredDate) ||
      (shipDate && inHandDate && shipDate > inHandDate)
    ) {
      return "danger";
    } else if (
      line.shipDateD365 &&
      line.shipDateD365 != "" &&
      line.trackingCount !== 0 &&
      (!line.inHandDate || line.inHandDate === "")
    ) {
      return "warning";
    }
    return "";
  }

  getLineTrackingClass(line: ProductionWorkflowLineDto) {
    if (
      line.shipDateD365 &&
      line.shipDateD365 !== "" &&
      line.trackingCount === 0
    ) {
      return "warning";
    }
    return "";
  }

  // fixes/14757: replaced by openAddTrackingFromList() which opens the unified jni-add-tracking popup instead
  // getManualShippingMethods() {
  //   this._productionWorkflowService
  //     .getManualTrackingShippingMethods()
  //     .pipe(untilDestroyed(this))
  //     .subscribe({
  //       next: res => {
  //         if (res?.data) {
  //           this.manualShippingMethods = res?.data;
  //         } else {
  //           this.manualShippingMethods = [];
  //         }
  //         this.manualMethod = "";
  //         this.trackings = "";
  //         this.displayAddManualTrackingModal = true;
  //       },
  //     });
  // }

  // openAddManualTrackingModal() {
  //   this.manualMethod = "";
  //   this.trackings = "";
  //   this.getManualShippingMethods();
  // }

  // onTrackingListAddSubmit(event: AddTrackingSubmitDto) {
  //   if (!event.trackingNumber) {
  //     this._toastr.error("Error", "Enter Tracking Number");
  //     return;
  //   }

  //   this._loaderService.setLoaderState(true);

  //   const orderLine = this.orderLines.find(
  //     x => x.lineRecIdD365 === this.trackingLineRecIdD365
  //   );

  //   const trackingData = [
  //     {
  //       Quantity: event.quantity,
  //       VendorSKU: String(orderLine?.externalItemNumberD365),
  //       LineNumber: orderLine?.lineNumberD365 ?? 0,
  //       TrackingNumber: event.trackingNumber,
  //       PurchaseOrder: String(orderLine?.purchaseOrderD365),
  //       IsMasterTracking: event.isMasterTracking,
  //       ShippingMethod: event.shippingMethod,
  //     },
  //   ];
  //   this.addTrackingData(trackingData, event.trackingType === "other");
  // }

  // addManualTrackingNumbers() {
  //   if (this.trackings === "") {
  //     this._toastr.error("Error", "Enter Tracking Numbers");
  //     return;
  //   }

  //   if (this.manualMethod === "" || !this.manualMethod) {
  //     this._toastr.error("Error", "Select a Shipping Method");
  //     return;
  //   }

  //   this._loaderService.setLoaderState(true);
  //   const trackingNumbers = this.trackings.split(",");

  //   const orderLine = this.orderLines.find(
  //     x => x.lineRecIdD365 === this.trackingLineRecIdD365
  //   );

  //   const trackingData = trackingNumbers.map((obj: string) => {
  //     return {
  //       Quantity: 0,
  //       VendorSKU: String(orderLine?.externalItemNumberD365),
  //       LineNumber: orderLine?.lineNumberD365 ?? 0,
  //       TrackingNumber: String(obj),
  //       PurchaseOrder: String(orderLine?.purchaseOrderD365),
  //       IsMasterTracking: false,
  //       ShippingMethod: this.manualMethod,
  //     };
  //   });
  //   this.addTrackingData(trackingData, true);
  // }

  updateManualTrackingStatus(event: ClientSideLineTrackingListDto) {
    if (!event) {
      this._loaderService.setLoaderState(false);
      this._toastr.error("Error", "Invalid Request");
      return;
    }

    const request = {
      trackingLineId: event.id,
      trackingNumber: event.trackingNumber,
      status: event.deliveryStatusId ?? 0,
      lineRecId: event.lineRecIdD365,
      workflowId: this.workflowId,
      vendorSKU: event.vendorSKU ?? "",
    } as UpdateManualTrackingStatusDto;

    this._productionWorkflowService
      .updateManualTrackingStatus(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res?.data) {
            this._toastr.success(
              "Success",
              "Tracking Status updated Successfully"
            );
            this.getWorflowLineTrackingList();
          } else {
            this._toastr.error("Error", "Failed to update Tracking Status");
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", "Failed to update Tracking Status");
        },
      });
  }

  createProductionTask(lineItem: ProductionWorkflowLineDto) {
    if (lineItem) {
      this.createProductionTaskEvent.emit(lineItem);
    }
  }

  refreshGrid() {
    this.cancelAllEditRow();
    this.refreshWorflowLineItemsEvent.emit();
  }

  onInputChange(event: boolean) {
    this.hasUnsavedChangesEvent.emit(event);
  }

  onWorkflowListESDDateEdit(
    shipdate: string,
    orderLine: ProductionWorkflowLineDto
  ) {
    const date =
      shipdate !== null && shipdate !== "" ? new Date(shipdate) : new Date();
    orderLine.shipDateD365 = this.datePipe.transform(date, "MM-dd-yyyy") ?? "";
    this.hasUnsavedChangesEvent.emit(true);
  }

  onWorkflowLineStatusChange(
    event: SelectChangeEvent,
    orderline: ProductionWorkflowLineDto
  ) {
    orderline.lineStatus = event.value;
    this.onRowEditSave(orderline);
  }
}
